import { ApiClientTradeBuildingSettings } from 'api/_legacy/generated-api'
import {
  ApiSwitchOutInstruction,
  useGetByTenantClientQuery,
  usePostByTenantNoteDataAccountAndAccountIdNewinstructionsMutation
} from 'api/rtk-api'
import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  Alert,
  ConfirmUsernameField,
  CurrencyFieldV2,
  OperationButton,
  RadioFieldSet,
  SelectField
} from '@/components/core-v2'
import { required } from '@/components/core-v2/molecules/fields/validators'
import { CURRENCY, TENANT } from '@/const'
import { formatCurrencyFull } from '@/utilities/currency'

import classes from './index.module.scss'

export interface TransferSwitchFormProps {
  accountId: string
  accountValue: number
  businessUnitId: string
  setModalData: (data: any) => void
  tradeIds: { label: string; value: string }[]
}
export enum FormIds {
  FullPartialId = 'fullPartialId',
  AmountId = 'amountId',
  TradeId = 'tradeId',
  UserNameId = 'usernameId'
}

export enum SwitchOption {
  Full = 'Full',
  Partial = 'Partial'
}

export const TransferSwitchForm = ({
  accountId,
  accountValue,
  businessUnitId,
  setModalData,
  tradeIds
}: TransferSwitchFormProps) => {
  const [settings, setSettings] = useState<ApiClientTradeBuildingSettings>(null)
  const {
    clearErrors,
    formState: { errors, isValid },
    getValues,
    handleSubmit,
    setValue
  } = useFormContext()

  const errorInfo = {
    [FormIds.AmountId]: {
      message: 'Please enter a lower amount',
      title: 'Warning, you cannot switch more than 95% of all funds'
    },
    classification: {
      message:
        'Please adjust the switch to ensure the tax classification of the trades is consistent with the note',
      title: 'Warning, invalid tax classification'
    }
  }

  const [switchFullPartial, switchToTrade, switchAmount] = useWatch({
    name: [FormIds.FullPartialId, FormIds.TradeId, FormIds.AmountId]
  })

  const [mutate, { data: instructionData, isLoading: isInstructionLoading }] =
    usePostByTenantNoteDataAccountAndAccountIdNewinstructionsMutation()

  const { data: clientData } = useGetByTenantClientQuery({ tenant: TENANT })

  useEffect(() => {
    if (clientData) {
      const settings = clientData.find(client => {
        const containsBusinessUnit = client.businessUnits.find(
          businessUnit => businessUnit.businessUnitId === businessUnitId
        )
        return Boolean(containsBusinessUnit)
      }).tradeBuildingSettings
      setSettings(settings)
    }
  }, [businessUnitId, clientData])

  useEffect(() => {
    if (getValues(FormIds.AmountId)) {
      setValue(FormIds.AmountId, undefined)
      clearErrors(FormIds.AmountId)
    }
  }, [clearErrors, getValues, setValue, switchFullPartial])

  useEffect(() => {
    const isFull = switchFullPartial === SwitchOption.Full
    if (switchToTrade && (isFull || switchAmount)) {
      const inst: ApiSwitchOutInstruction = {
        accountId,
        isFullSwitch: isFull,
        switchAmount: isFull ? null : Number(switchAmount.replace(/,/g, '')),
        targetAccountId: switchToTrade,
        type: 'SwitchOut',
        valuationCycle: new Date().toISOString().substring(0, 10)
      }
      mutate({
        accountId,
        apiInstructionSet: {
          portfolioChanges: [],
          deposits: [],
          withdrawals: [],
          switchOutInstructions: [inst]
        },
        tenant: TENANT
      })
    }
  }, [mutate, switchAmount, switchFullPartial, switchToTrade, accountId])

  const classificationsMismatch = instructionData =>
    instructionData &&
    instructionData.noteTaxClassification !== instructionData.newCalculatedNoteTaxClassifications

  const maxValidValue = settings && settings.minimumTopUpSize <= accountValue * 0.95

  return (
    <form
      className={classes.transferSwitch}
      onSubmit={handleSubmit(data => {
        const isFull = switchFullPartial === SwitchOption.Full
        setModalData({
          ...data,
          accountName: tradeIds.find(id => id.value === data[FormIds.TradeId]).label,
          [FormIds.AmountId]: isFull
            ? accountValue
            : Number(data[FormIds.AmountId].replace(/,/g, ''))
        })
      })}
    >
      <RadioFieldSet
        legend="Choose an option"
        name={FormIds.FullPartialId}
        options={[
          { label: 'Full switch (100% of the trade)', value: SwitchOption.Full },
          { label: 'Partial switch (up to 95% of the trade)', value: SwitchOption.Partial }
        ]}
      />
      {switchFullPartial === SwitchOption.Partial && (
        <>
          {maxValidValue && (
            <CurrencyFieldV2
              className={classes.switchAmount}
              id={FormIds.AmountId}
              label={`How much to switch (Minimum ${formatCurrencyFull(
                settings?.minimumTopUpSize,
                CURRENCY
              )} - Maximum ${formatCurrencyFull(accountValue * 0.95, CURRENCY)})`}
              validators={{
                validate: {
                  required: (value: string) => Boolean(value) || 'Amount is required',
                  aboveMin: (value: string) => {
                    if (!value) return false
                    const num = Number(value.replace(/,/g, ''))
                    return (
                      (!Number.isNaN(num) && num > 0 && settings?.minimumTopUpSize <= num) ||
                      `Amount has to be more than ${settings?.minimumTopUpSize}`
                    )
                  },
                  below95: (value: string) => {
                    if (!value) return false
                    const num = Number(value.replace(/,/g, ''))
                    return !Number.isNaN(num) && num > 0 && num <= accountValue * 0.95
                  }
                }
              }}
            />
          )}
          {!maxValidValue && (
            <Alert className="mb-7" title="Partial Switch is not available" type="error">
              Value of Switch Out Trade is too low
            </Alert>
          )}
        </>
      )}
      <SelectField
        className={classes.tradeOption}
        id={FormIds.TradeId}
        label="Choose a trade to switch into"
        options={tradeIds}
        placeholderOption={{ label: 'Choose a trade', value: '' }}
        validators={required}
      />
      {errors[FormIds.AmountId]?.type === 'below95' && (
        <Alert className="mb-7" title={errorInfo[FormIds.AmountId].title} type="error">
          {errorInfo[FormIds.AmountId].message}
        </Alert>
      )}
      {!errors[FormIds.AmountId] && classificationsMismatch(instructionData) && (
        <Alert className="mb-7" title={errorInfo.classification.title} type="error">
          {errorInfo.classification.message}
        </Alert>
      )}
      <ConfirmUsernameField label="To submit the switch enter your username and click on the button" />
      <OperationButton
        isDisabled={
          !isValid ||
          !maxValidValue ||
          isInstructionLoading ||
          classificationsMismatch(instructionData)
        }
        operationType="progress"
        type="submit"
      >
        Submit
      </OperationButton>
    </form>
  )
}
